import React, { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, List, ListItem, ListItemText, Typography } from '@mui/material';
import { format } from 'date-fns';
import { NoticeService } from '../../services/notice.service';
import { INoticeReader } from '../../interfaces/notice.interface';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';

interface Props {
    open: boolean;
    noticeId: string | null;
    onClose: () => void;
}

const NoticeReadersDialog: React.FC<Props> = ({ open, noticeId, onClose }) => {
    const [readers, setReaders] = useState<INoticeReader[]>([]);
    const [loading, setLoading] = useState(false);
    const toast = useToast();
    const institutionId = AuthService.getSelectedInstitution()!;

    useEffect(() => {
        if (!open || !noticeId) return;
        setLoading(true);
        NoticeService.getReaders(noticeId, institutionId)
            .then(setReaders)
            .catch(err => toast.showApiError(err, 'Failed to load readers'))
            .finally(() => setLoading(false));
    }, [open, noticeId]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ 'data-help-page': 'notices.readers.page' } as any}>
            <DialogTitle>Readers ({readers.length})</DialogTitle>
            <DialogContent>
                {loading ? (
                    <Typography>Loading...</Typography>
                ) : readers.length === 0 ? (
                    <Typography color="text.secondary" data-help="notices.readers.empty">No one has read this notice yet.</Typography>
                ) : (
                    <List dense data-help-section="notices.readers.list">
                        {readers.map(r => (
                            <ListItem key={r.userId}>
                                <ListItemText
                                    primary={r.username || r.userId}
                                    secondary={format(new Date(r.readAt), 'dd MMM yyyy HH:mm')}
                                />
                            </ListItem>
                        ))}
                    </List>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} data-help="notices.readers.close">Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default NoticeReadersDialog;
